import React from 'react';
import { ExternalLink, Linkedin, Twitter, Youtube, Instagram, Facebook, Github, MessageCircle, Music, Globe } from 'lucide-react';
import { Card } from './ui/card';
import { Button } from './ui/button';

const SocialHub = () => {
  const socialLinks = [
    {
      name: 'Facebook',
      label: 'charlesn888',
      url: 'https://www.facebook.com/charlesn888',
      color: '#1877F2',
      icon: Facebook,
    },
    {
      name: 'Instagram',
      label: '@charlesn888',
      url: 'https://www.instagram.com/charlesn888/',
      color: '#E4405F',
      icon: Instagram,
    },
    {
      name: 'X (Twitter)',
      label: '@charlesngo888',
      url: 'https://x.com/charlesngo888',
      color: '#000000',
      icon: Twitter,
    },
    {
      name: 'LinkedIn',
      label: 'charles-ngo-world-peace-now',
      url: 'https://www.linkedin.com/in/charles-ngo-world-peace-now/',
      color: '#0A66C2',
      icon: Linkedin,
    },
    {
      name: 'YouTube',
      label: '@worldpeacenow888',
      url: 'https://www.youtube.com/@worldpeacenow888',
      color: '#FF0000',
      icon: Youtube,
    },
    {
      name: 'TikTok',
      label: '@charlesn888',
      url: 'https://www.tiktok.com/@charlesn888',
      color: '#000000',
      icon: Music,
    },
    {
      name: 'Pinterest',
      label: 'charlesn888',
      url: 'https://www.pinterest.com/charlesn888/',
      color: '#E60023',
      icon: Globe,
    },
    {
      name: 'Threads',
      label: '@charlesn888',
      url: 'https://www.threads.net/@charlesn888',
      color: '#000000',
      icon: MessageCircle,
    },
    {
      name: 'Bluesky',
      label: 'charlesn888.bsky.social',
      url: 'https://bsky.app/profile/charlesn888.bsky.social',
      color: '#1185FE',
      icon: Globe,
    },
  ];

  return (
    <section id="social" className="py-24 bg-gradient-to-b from-[#0B1D2A] to-black relative overflow-hidden">
      {/* Background elements */}
      <div className="absolute top-0 left-0 w-96 h-96 bg-[#D4AF37]/5 rounded-full blur-3xl" />
      <div className="absolute bottom-0 right-0 w-96 h-96 bg-[#D4AF37]/5 rounded-full blur-3xl" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2
            className="text-4xl sm:text-5xl font-bold text-white mb-4"
            style={{ fontFamily: 'Montserrat, sans-serif' }}
          >
            Connect & <span className="text-[#D4AF37]">Collaborate</span>
          </h2>
          <div className="w-24 h-1 bg-[#D4AF37] mx-auto rounded-full mb-6" />
          <p
            className="text-lg text-white/70 max-w-2xl mx-auto"
            style={{ fontFamily: 'Lato, sans-serif' }}
          >
            Join the conversation across multiple platforms. Let's build a world that works for everyone, together.
          </p>
        </div>

        {/* Social Links Grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-16">
          {socialLinks.map((link, index) => {
            const Icon = link.icon;
            return (
              <Card
                key={index}
                className="bg-[#0B1D2A]/60 border-[#D4AF37]/20 p-6 transition-all duration-300 hover:transform hover:scale-105 backdrop-blur-sm group"
                style={{ borderColor: `${link.color}20` }}
              >
                <a
                  href={link.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-start gap-4"
                >
                  <div className="flex-shrink-0">
                    <div 
                      className="w-12 h-12 rounded-full flex items-center justify-center transition-colors"
                      style={{ backgroundColor: `${link.color}15` }}
                    >
                      <Icon className="w-6 h-6" style={{ color: link.color }} />
                    </div>
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <h3
                        className="text-white font-bold text-lg"
                        style={{ fontFamily: 'Montserrat, sans-serif' }}
                      >
                        {link.name}
                      </h3>
                      <ExternalLink className="w-4 h-4 text-[#D4AF37] opacity-0 group-hover:opacity-100 transition-opacity" />
                    </div>
                    <p
                      className="text-white/70 text-sm"
                      style={{ fontFamily: 'Lato, sans-serif' }}
                    >
                      {link.label}
                    </p>
                  </div>
                </a>
              </Card>
            );
          })}
        </div>

        {/* Live Social Feed Section */}
        <div className="mb-16">
          <Card className="bg-[#0a192f]/60 border-[#D4AF37]/30 p-8 backdrop-blur-sm">
            <h3
              className="text-3xl font-bold text-[#D4AF37] mb-4 text-center"
              style={{ fontFamily: 'Montserrat, sans-serif' }}
            >
              Live Movement Updates
            </h3>
            <p
              className="text-white/70 text-center mb-8 text-lg"
              style={{ fontFamily: 'Lato, sans-serif' }}
            >
              Real-time updates from across our social platforms
            </p>
            
            {/* Juicer.io Feed Container */}
            <div className="bg-[#0a192f]/80 rounded-lg p-4">
              <ul className="juicer-feed" data-feed-id="charles-crawshaw"></ul>
            </div>
          </Card>
        </div>

        {/* Call to Action */}
        <div className="mt-16 text-center">
          <Card className="bg-gradient-to-br from-[#D4AF37]/20 via-[#0B1D2A]/80 to-black border-2 border-[#D4AF37]/50 p-8 backdrop-blur-md">
            <p
              className="text-2xl font-semibold text-white mb-6"
              style={{ fontFamily: 'Montserrat, sans-serif' }}
            >
              Follow the journey. Join the <span className="text-[#D4AF37]">movement</span>.
            </p>
            <p
              className="text-white/80 mb-8"
              style={{ fontFamily: 'Lato, sans-serif' }}
            >
              Stay connected for insights on AI, transformation, and the path to world peace.
            </p>
            <Button
              onClick={() => {
                const contactSection = document.getElementById('contact');
                if (contactSection) {
                  const offset = 80;
                  const elementPosition = contactSection.offsetTop - offset;
                  window.scrollTo({ top: elementPosition, behavior: 'smooth' });
                }
              }}
              className="bg-[#D4AF37] hover:bg-[#D4AF37]/90 text-black font-bold px-8 py-6 text-lg rounded-lg transition-all duration-300 hover:scale-105 hover:shadow-xl hover:shadow-[#D4AF37]/50"
              style={{ fontFamily: 'Lato, sans-serif' }}
            >
              Get In Touch
            </Button>
          </Card>
        </div>
      </div>
    </section>
  );
};

export default SocialHub;
